/**
 * Utility functions for managing empty state content across different node types
 * Provides consistent messaging and content for various data resource states
 */

export interface EmptyStateContent {
  title: string;
  subtitle: string;
}

/**
 * Gets appropriate empty state content based on node type
 * @param nodeType - The type of node (data_set, api_result, transform, etc.)
 * @returns Object with title, subtitle, and optional action text
 */
export const getEmptyStateContent = (nodeType: string = 'default'): EmptyStateContent => {
  const contentByNodeType: Record<string, EmptyStateContent> = {
    api_result: {
      title: 'No Schema & Samples Available for API Result',
      subtitle:
        'This API Result has no sample data yet. Run a test on the upstream API Request node to generate samples.',
    },
    api_error: {
      title: 'No Schema & Samples Available for API Error',
      subtitle:
        'This API Error node has no sample data yet. Run a test on the upstream API Request node to trigger error samples.',
    },
    api_request: {
      title: 'No Schema & Samples Available for API Request',
      subtitle:
        'This API Request has no sample data yet. Configure the request parameters and run a test.',
    },
    api_success_modifier: {
      title: 'No Schema & Samples Available for API Transform',
      subtitle:
        'This API Transform has no sample data yet. Run a test on the upstream API Result node to generate samples.',
    },
    splitter_output: {
      title: 'No Schema & Samples Available for Splitter Output',
      subtitle: 'Run a test on the upstream API Request node to generate samples.',
    },
    data_set: {
      title: 'No Schema & Samples Available for Nexset',
      subtitle:
        "This Nexset doesn't have a schema or sample data yet. Data will appear here once the source connection is established and data is detected.",
    },
    default: {
      title: 'No Schema & Samples Available',
      subtitle: "This resource doesn't have any schema or sample data available yet.",
    },
  };

  const content = contentByNodeType[nodeType] || contentByNodeType.default;
  return content;
};

/**
 * Gets contextual empty state content based on what's missing
 * Note: This function no longer handles "No Live Samples" cases - those should never be shown.
 * If schema exists, the table should always be shown regardless of samples.
 */
export const getContextualEmptyStateContent = (context) => {
  const { hasSchema, hasSamples, nodeType } = context;

  // If schema exists, always show the table (don't show empty state)
  if (hasSchema) return null;

  // Only show empty state if there's no schema
  return getEmptyStateContent(nodeType);
};
